import React, { useCallback, useEffect, useState } from 'react'
import { FileModel, RoomEventType, RoomSession } from '@zocks-communications/client-sdk-js'
import * as Sentry from '@sentry/react'
import './FinancialAdvisorSummary.scss'
import { useParams } from 'react-router-dom'

import { TranscribeImportedSession, UserTranscriptionMapping } from './ImportSession'
import { FinancialAdvisorSummaryProps, RoomSessionContext } from './models'
import { TrackEvent, useTracking } from './common/tracking'
import { useAPIClient } from './APIClientProvider'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../store/auth/authSlice'
import { AssistantNotJoined } from './AssistantNotJoined'
import { useGetRoomQuery } from './store/fa'
import { useLazyGetUserByIdQuery } from '../../../store/user/user'
import { User } from '../../../models/User'
import { useListAiResultsQuery } from '../../../store/media/media'
import { MeetingSummary } from './MeetingSummary/MeetingSummary'
import { MeetingPreparation } from './MeetingPreparation/MeetingPreparation'
import { parseMetadata } from '../../../common/util'
import { PrivateMeetingNotAccessible } from './PrivateMeetingNotAccessible'
import { ApiError, isApiErrors } from '../../../models/ApiErrors'
import { isEnhancedAllocatedSessionTranscription } from './utils'

const _SUPPORTED_SERVICE_IDS = ['zoom']

interface PagesDetails {
  body: React.ReactElement
}

interface Pages {
  live: PagesDetails
  upcoming: PagesDetails
  loading: PagesDetails
  summary: PagesDetails
  transcribe: PagesDetails
  userMapping: PagesDetails
  assistantNotJoined: PagesDetails
  privateMeetingNotAccessible: PagesDetails
}

export const FinancialAdvisorSummary: React.FC<FinancialAdvisorSummaryProps> = ({ roomId: roomIdParam, sessionId: sessionIdParam }) => {
  const { mediaClient } = useAPIClient()
  const accountId = useAppSelector(selectAccountId)
  const user = useAppSelector(selectAuthenticatedUser)
  const { trackingClient } = useTracking()

  const { roomId = roomIdParam, sessionId = sessionIdParam } = useParams()
  const [roomSession, setRoomSession] = useState<RoomSession | null>(null)
  const [currentPage, setCurrentPage] = useState<keyof Pages>('loading')
  const [assistantErrorReason, setAssistantErrorReason] = useState<string | null>(null)
  const [transcriptionFile, setTranscriptionFile] = useState<FileModel | null>(null)
  const { data: room } = useGetRoomQuery({ accountId: accountId, roomId: roomId! })
  const { data: aiResults } = useListAiResultsQuery(
    { accountId, roomId: roomId!, sessionId: sessionId!, pageRequest: { page: 0, size: 1 } },
    { refetchOnMountOrArgChange: true },
  )
  const [triggerGetUserById] = useLazyGetUserByIdQuery()
  const [roomCreatorUser, setRoomCreatorUser] = useState<User | null>(null)
  useEffect(() => {
    if (room && room.createdBy.id) {
      triggerGetUserById({ accountId, userId: room.createdBy.id }).then((response) => {
        if (response.data) {
          setRoomCreatorUser(response.data.data)
        }
      })
    }
  }, [accountId, room, triggerGetUserById])

  useEffect(() => {
    setRoomSession(null)
    setCurrentPage('loading')
    if (roomId && sessionId) {
      mediaClient
        .getRoomSession(roomId, sessionId)
        .then((result) => {
          setRoomSession(result)
        })
        .catch((e: unknown) => {
          if (isApiErrors(e)) {
            if (e.errors.some((err: ApiError) => err.message.toLowerCase().includes('cannot access private meeting'))) {
              setCurrentPage('privateMeetingNotAccessible')
            }
          }
        })
        .finally(() => {})
    }
  }, [roomId, sessionId, mediaClient])

  const getSource = (session: RoomSession) => {
    try {
      if (session.metadata) {
        const metadata = JSON.parse(session.metadata)
        return metadata.source || 'external'
      }
    } catch (e) {
      console.error(e)
    }
    return session.kind === 'external' ? 'external' : null
  }

  const getImportPage = useCallback(async () => {
    if (!roomSession) {
      return null
    }
    const source = getSource(roomSession)
    if (source !== 'import') {
      return null
    }

    const files = await mediaClient.listSessionFiles(roomSession.roomId, roomSession.id)
    const transcriptionFile = files.find(isEnhancedAllocatedSessionTranscription)
    if (!transcriptionFile) {
      return 'transcribe'
    }
    setTranscriptionFile(transcriptionFile)
    const fileMetadata = JSON.parse(transcriptionFile.metadata)
    if (!fileMetadata.allocatedOnImport) {
      return 'userMapping'
    }
    return 'summary'
  }, [mediaClient, roomSession])

  const getPageByResults = useCallback(() => {
    const sessionHasAiResult = !!aiResults?.data?.length
    if (sessionHasAiResult) {
      return 'summary'
    }
    return null
  }, [aiResults?.data?.length])

  const getPageByTime = useCallback(() => {
    if (!roomSession) {
      return null
    }
    const now = new Date().toISOString()
    if (((!roomSession.finishedAt || roomSession.finishedAt > now) && roomSession.startedAt <= now) || roomSession.isActive) {
      return 'live'
    }
    if (roomSession.startedAt > now) {
      return 'upcoming'
    }
    return null
  }, [roomSession])

  const assistantShouldHaveJoinedMeeting = (creator: User, session: RoomSession) => {
    const metadata = parseMetadata(session.metadata)
    const { source: serviceId, botStartTrigger } = metadata
    if (botStartTrigger === 'adhoc') {
      return true
    }

    const sessionAutoJoin = serviceId && _SUPPORTED_SERVICE_IDS.includes(serviceId) && session.config?.autoJoin
    const autoJoinIntegrations =
      creator.integrations?.some(
        (integration) => integration.serviceId === serviceId && integration.configuration.type === 'bot' && integration.configuration.autoJoin,
      ) ?? false
    return autoJoinIntegrations || sessionAutoJoin
  }

  const getPageByEvents = useCallback(async () => {
    if (!roomSession || !roomCreatorUser) {
      return null
    }
    if (roomSession.kind !== 'external' || !assistantShouldHaveJoinedMeeting(roomCreatorUser, roomSession)) {
      return 'summary'
    }

    const events = await mediaClient.listRoomSessionEvents(roomSession.roomId, roomSession.id, [
      RoomEventType.ASSISTANT_JOINED_WAITING_ROOM,
      RoomEventType.ASSISTANT_ADMITTED_FROM_WAITING_ROOM,
      RoomEventType.ASSISTANT_LEFT_WAITING_ROOM,
      RoomEventType.ASSISTANT_JOINED,
    ])
    const assistantJoined = events.some((event) => event.type === RoomEventType.ASSISTANT_JOINED)
    if (assistantJoined) {
      return 'summary'
    }
    const joinedWaitingRoomEvents = events.some((event) => event.type === RoomEventType.ASSISTANT_JOINED_WAITING_ROOM)
    const assistantAdmittedEvents = events.some((event) => event.type === RoomEventType.ASSISTANT_ADMITTED_FROM_WAITING_ROOM)
    if (joinedWaitingRoomEvents) {
      if (assistantAdmittedEvents) {
        setAssistantErrorReason(
          'Sorry, there was technical issues and the Assistant could never join to the meeting, therefore we could not generate any summary or Intelligence results. Please contact the support at support@zocks.io.',
        )
      } else {
        setAssistantErrorReason(
          'Assistant was removed or never admitted from the waiting room, therefore we could not generate any summary or Intelligence results.',
        )
      }
    } else {
      const source = getSource(roomSession)
      if (source === 'zoom' && !roomSession.users.map((user) => user.id).includes(roomCreatorUser.id)) {
        setAssistantErrorReason('Host with assistant did not join the meeting, therefore we could not generate any summary or Intelligence results.')
      } else {
        setAssistantErrorReason('Assistant did not join the meeting, therefore we could not generate any summary or Intelligence results.')
      }
    }
    return 'assistantNotJoined'
  }, [mediaClient, roomCreatorUser, roomSession])

  useEffect(() => {
    if (roomSession == null) {
      return
    }
    Sentry.setTags({
      roomId: roomSession.roomId,
      sessionId: roomSession.id,
    })
  }, [roomSession])

  useEffect(() => {
    const calculateAndSetCurrentPage = async () => {
      setCurrentPage((await getImportPage()) || getPageByResults() || getPageByTime() || (await getPageByEvents()) || 'summary')
    }
    if (!roomSession || !roomCreatorUser || !aiResults?.data) {
      return
    }
    calculateAndSetCurrentPage().catch(console.error)
  }, [roomSession, roomCreatorUser, aiResults, getImportPage, getPageByResults, getPageByTime, getPageByEvents])

  useEffect(() => {
    if (roomId && sessionId && !user?.isGhosted) {
      trackingClient.track({
        eventName: TrackEvent.ROOM_SESSION_OPENED,
        eventDetails: {
          roomId,
          sessionId,
        },
      })
    }
  }, [roomId, sessionId, trackingClient, user?.isGhosted])

  const onTranscribeFinished = (file: FileModel) => {
    setTranscriptionFile(file)
    setCurrentPage('userMapping')
  }

  const onUserMappingSaved = () => {
    if (!roomSession) {
      return
    }
    const { roomId, id: sessionId } = roomSession
    mediaClient.getRoomSession(roomId, sessionId).then(setRoomSession).catch(console.error)
    setCurrentPage('summary')
  }

  const onSessionFinished = useCallback(() => {
    if (!roomSession) {
      return
    }
    const { roomId, id: sessionId } = roomSession
    mediaClient.getRoomSession(roomId, sessionId).then(setRoomSession).catch(console.error)
  }, [mediaClient, roomSession])

  const pages: Pages = {
    live: { body: <MeetingPreparation onSessionFinished={onSessionFinished} /> },
    upcoming: { body: <MeetingPreparation onSessionFinished={onSessionFinished} /> },
    loading: { body: <div className='skeleton' style={{ width: '100%', height: '60px' }} /> },
    summary: {
      body: <MeetingSummary />,
    },
    transcribe: {
      body: <TranscribeImportedSession roomId={roomId} sessionId={sessionId} onTranscribeFinished={onTranscribeFinished} />,
    },
    userMapping: {
      body: <UserTranscriptionMapping roomSession={roomSession} transcriptionFile={transcriptionFile} onUserMappingSaved={onUserMappingSaved} />,
    },
    assistantNotJoined: {
      body: <AssistantNotJoined reason={assistantErrorReason} />,
    },
    privateMeetingNotAccessible: {
      body: <PrivateMeetingNotAccessible />,
    },
  }

  return (
    <RoomSessionContext.Provider value={roomSession}>
      <div className='w-100'>{pages[currentPage].body}</div>
    </RoomSessionContext.Provider>
  )
}
